/**
 * MapLibre sources and layers for d-TPP approach-plate overlays.
 *
 * - One shared GeoJSON source ('approach-plates-quads') holds every
 *   plate's footprint polygon; fill/line layers render the outlines.
 * - One MapLibre canvas source per selected plate renders the PDF
 *   (via pdfjs-dist) at its quad corners.
 */

import type { FeatureCollection, Polygon } from 'geojson';
import type { LayerSpecification, Map as MaplibreMap, GeoJSONSource } from 'maplibre-gl';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { getAuthHeader } from '../services/AuthContext';
import { getChart, storeChart } from '../services/CacheService';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

export const PLATES_OUTLINE_SOURCE_ID = 'approach-plates-quads';
export const PLATES_OUTLINE_FILL_LAYER_ID = 'approach-plates-quads-fill';
export const PLATES_OUTLINE_LINE_LAYER_ID = 'approach-plates-quads-line';

export const plateImageSourceId = (featureId: string) => `approach-plate-img-${featureId}`;
export const plateImageLayerId = (featureId: string) => `approach-plate-img-${featureId}-layer`;

type QuadCorners = [[number, number], [number, number], [number, number], [number, number]];

export interface ChartCacheKey {
  cycle: string;
  pdfName: string;
}

// ─── Outline source + layers ─────────────────────────────────────────────────

export function createPlatesOutlineFillLayer(): LayerSpecification {
  return {
    id: PLATES_OUTLINE_FILL_LAYER_ID,
    type: 'fill',
    source: PLATES_OUTLINE_SOURCE_ID,
    layout: { visibility: 'visible' },
    paint: { 'fill-color': '#22D3EE', 'fill-opacity': 0.08 },
  } as LayerSpecification;
}

export function createPlatesOutlineLineLayer(): LayerSpecification {
  return {
    id: PLATES_OUTLINE_LINE_LAYER_ID,
    type: 'line',
    source: PLATES_OUTLINE_SOURCE_ID,
    layout: { visibility: 'visible' },
    paint: {
      'line-color': '#0E7490',
      'line-width': 1.5,
      'line-dasharray': [3, 2],
    },
  } as LayerSpecification;
}

export function installPlatesOutline(map: MaplibreMap, data: FeatureCollection): void {
  const existing = map.getSource(PLATES_OUTLINE_SOURCE_ID) as GeoJSONSource | undefined;
  if (existing) { existing.setData(data); return; }
  map.addSource(PLATES_OUTLINE_SOURCE_ID, { type: 'geojson', data });
  map.addLayer(createPlatesOutlineFillLayer());
  map.addLayer(createPlatesOutlineLineLayer());
}

export function removePlatesOutline(map: MaplibreMap): void {
  for (const id of [PLATES_OUTLINE_FILL_LAYER_ID, PLATES_OUTLINE_LINE_LAYER_ID]) {
    if (map.getLayer(id)) map.removeLayer(id);
  }
  if (map.getSource(PLATES_OUTLINE_SOURCE_ID)) {
    map.removeSource(PLATES_OUTLINE_SOURCE_ID);
  }
}

// ─── PDF rendering ───────────────────────────────────────────────────────────

/**
 * Render a PDF page onto an offscreen canvas suitable for a MapLibre
 * canvas source. `scale` controls the raster resolution.
 */
export async function renderPdfPageToCanvas(
  pdfUrl: string,
  pageNumber = 1,
  scale = 2,
  cacheKey?: ChartCacheKey,
): Promise<HTMLCanvasElement> {
  const pdf = await pdfjsLib.getDocument(cacheKey
    ? { data: new Uint8Array(await (await loadChartBlob(pdfUrl, cacheKey)).arrayBuffer()) }
    : { url: pdfUrl, httpHeaders: getAuthHeader() }).promise;
  const page = await pdf.getPage(pageNumber);
  const viewport = page.getViewport({ scale });

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context not available');

  await page.render({ canvas, canvasContext: ctx, viewport }).promise;
  return canvas;
}

async function loadChartBlob(url: string, cacheKey?: ChartCacheKey): Promise<Blob> {
  if (cacheKey) {
    const cached = await getChart(cacheKey.cycle, cacheKey.pdfName).catch(() => null);
    if (cached) return cached;
  }

  const res = await fetch(url, { headers: getAuthHeader() });
  if (!res.ok) throw new Error(`Chart fetch error ${res.status}: ${res.statusText}`);
  const blob = await res.blob();
  if (cacheKey) {
    await storeChart(cacheKey.cycle, cacheKey.pdfName, blob).catch(() => { /* cache is best effort */ });
  }
  return blob;
}

function imageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Chart PNG image could not be decoded'));
    };
    image.src = url;
  });
}

export async function renderPngPageToCanvas(pngUrl: string, cacheKey?: ChartCacheKey): Promise<HTMLCanvasElement> {
  const image = await imageFromBlob(await loadChartBlob(pngUrl, cacheKey));
  const canvas = document.createElement('canvas');
  canvas.width = image.naturalWidth || image.width;
  canvas.height = image.naturalHeight || image.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D canvas context not available');
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas;
}

export async function renderChartPageToCanvas({
  pngUrl,
  pdfUrl,
  pageNumber = 1,
  scale = 2,
  cacheKey,
}: {
  pngUrl?: string;
  pdfUrl: string;
  pageNumber?: number;
  scale?: number;
  cacheKey?: ChartCacheKey;
}): Promise<HTMLCanvasElement> {
  if (pngUrl) {
    try {
      return await renderPngPageToCanvas(pngUrl, cacheKey && {
        cycle: cacheKey.cycle,
        pdfName: `${cacheKey.pdfName}/pages/${pageNumber}.png`,
      });
    } catch (err) {
      console.warn('[efb] chart PNG render failed; falling back to PDF.js', err);
    }
  }
  return renderPdfPageToCanvas(pdfUrl, pageNumber, scale, cacheKey);
}

// ─── Per-plate canvas overlay ────────────────────────────────────────────────

/**
 * Extract the 4 quad corners from a d-TPP Polygon ring.
 * The API writes rings in TL, TR, BR, BL, TL order; MapLibre canvas/
 * image sources expect coordinates in that same order.
 */
export function quadCornersFromPolygon(geometry: Polygon): QuadCorners {
  const ring = geometry.coordinates?.[0];
  if (!ring || ring.length < 5) {
    throw new Error('d-TPP quad geometry must have a closed 5-point ring');
  }
  return [
    ring[0] as [number, number],
    ring[1] as [number, number],
    ring[2] as [number, number],
    ring[3] as [number, number],
  ];
}

export function addPlateCanvasOverlay(
  map: MaplibreMap,
  featureId: string,
  canvas: HTMLCanvasElement,
  corners: QuadCorners,
  opacity = 0.85,
): void {
  const sourceId = plateImageSourceId(featureId);
  const layerId = plateImageLayerId(featureId);
  removePlateCanvasOverlay(map, featureId);
  map.addSource(sourceId, { type: 'canvas', canvas, coordinates: corners, animate: false });
  map.addLayer({
    id: layerId,
    type: 'raster',
    source: sourceId,
    paint: { 'raster-opacity': opacity, 'raster-fade-duration': 0 },
  } as LayerSpecification);
}

export function removePlateCanvasOverlay(map: MaplibreMap, featureId: string): void {
  const sourceId = plateImageSourceId(featureId);
  const layerId = plateImageLayerId(featureId);
  if (map.getLayer(layerId)) map.removeLayer(layerId);
  if (map.getSource(sourceId)) map.removeSource(sourceId);
}

export function removeAllPlateOverlays(map: MaplibreMap): void {
  const style = map.getStyle();
  for (const layer of style.layers ?? []) {
    if (layer.id.startsWith('approach-plate-img-')) {
      try { map.removeLayer(layer.id); } catch { /* ok */ }
    }
  }
  for (const id of Object.keys(style.sources ?? {})) {
    if (id.startsWith('approach-plate-img-')) {
      try { map.removeSource(id); } catch { /* ok */ }
    }
  }
}
